import { useState } from "preact/compat"
import { IntegrationWithJobInfo, updateIntegration } from "@/api/integration"
import { LedgerAccountJson, listLedgerAccounts } from "@/api/accounting.tsx"
import { AutocompleteSelect, SelectItem } from "@/accounting/autocomplete_select.tsx"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { toast } from "@/components/ui/use-toast"
import { useAsyncEffect } from "@/utils"

interface NetsuiteClearingAccountSettingsProps {
  integration: IntegrationWithJobInfo
}

interface NetsuiteAccountSettingProps {
  integration: IntegrationWithJobInfo
  /** The `NetsuiteConfig` field holding the account's NetSuite internal id. */
  config_key: "clearing_account_remote_id" | "inter_customer_clearing_account_remote_id"
  label: string
  description: string
}

/** One GL-account picker saved onto the NetSuite config by internal id. */
function NetsuiteAccountSetting({ integration, config_key, label, description }: NetsuiteAccountSettingProps) {
  const config = integration.data as Record<string, string | null | undefined>
  const [accounts, setAccounts] = useState<LedgerAccountJson[]>([])
  const [selected, setSelected] = useState<string | null>(config[config_key] ?? null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useAsyncEffect(async () => {
    const res = await listLedgerAccounts(true)
    if (res.ok) {
      setAccounts(res.value.data)
    }
    setLoading(false)
  }, [integration.id])

  async function handleSave() {
    setSaving(true)
    const res = await updateIntegration(integration.id, { data: { [config_key]: selected } })
    setSaving(false)
    if (!res.ok) {
      toast({ title: "Save failed", description: `Could not save the ${label.toLowerCase()}.`, variant: "destructive" })
      return
    }
    toast({ title: "Settings saved", description: `${label} updated.` })
  }

  if (loading) {
    return <div class="text-gray-500 text-sm">Loading GL accounts…</div>
  }

  const options: SelectItem[] = accounts
    .filter((a: LedgerAccountJson) => !!a.remote_id)
    .map((a: LedgerAccountJson) => ({ id: a.remote_id, name: a.expense_account, account_number: a.account_number }))
  const selected_option = selected ? options.find((o: SelectItem) => o.id === selected) : undefined

  return (
    <div class="space-y-3">
      <p class="text-sm text-gray-500">{description}</p>
      <div class="space-y-1 max-w-md">
        <Label>{label}</Label>
        <AutocompleteSelect values={options} initial={selected_option} onSelect={(item: SelectItem) => setSelected(item.id)} />
      </div>
      <Button variant="outline" disabled={saving || !selected} onClick={handleSave}>
        {saving ? "Saving…" : `Save ${label.toLowerCase()}`}
      </Button>
    </div>
  )
}

/**
 * Lets the user pick the trade-spend clearing/suspense account used by the month-end journal-entry
 * flow: when a check's cash was already applied in NetSuite via a lump credit memo, the JE credits
 * this account (it must be the account that credit memo hit) and debits the coded deduction GLs.
 */
export function NetsuiteClearingAccountSettings({ integration }: NetsuiteClearingAccountSettingsProps) {
  return (
    <NetsuiteAccountSetting
      integration={integration}
      config_key="clearing_account_remote_id"
      label="Trade-spend clearing account"
      description="When a check's cash was already applied in NetSuite (a lump credit memo exists), the month-end journal entry credits this clearing account and debits the coded deduction GL accounts. It must be the account those credit memos are booked to."
    />
  )
}

/**
 * The inter-customer clearing account a multi-customer cash application moves a deficit through
 * when one customer's side of a check nets negative (an Ahold payment covering two vendor
 * accounts): a dummy invoice on that customer and a dummy credit memo on the one whose cash
 * covered it, both on the NetSuite item mapped to this account.
 */
export function NetsuiteInterCustomerClearingSettings({ integration }: NetsuiteClearingAccountSettingsProps) {
  return (
    <NetsuiteAccountSetting
      integration={integration}
      config_key="inter_customer_clearing_account_remote_id"
      label="Inter-customer clearing account"
      description="When one payment covers several of your NetSuite customers and one customer's deductions exceed its invoices, the difference moves through this account: a dummy invoice on the customer that nets negative (settled by its credit memo) and a dummy credit memo of the same amount on the customer whose cash covered it (applied on its payment). The two always net to zero here; deductions themselves still post to open deductions. Pick your AR clearing account — its GL account needs a NetSuite item mapped."
    />
  )
}
